import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { ShareAlignmentFormDialog as ShareAlignmentFormDialogView } from './ShareAlignmentFormDialog.component';
import { alignmentAccessQueryOptions } from './alignment-access-query';
import { buildAlignmentFormLink, buildAlignmentInviteMessage } from './alignment-invite-message';

/** Figma / AC-10 — o botão fica em "Mensagem copiada" por 8 s. */
export const COPIED_LABEL_MS = 8_000;
/**
 * ALN-013c (retorno da QA, item 1.6) — clique duplo em "Copiar mensagem" é UMA
 * cópia: além da guarda de cópia em andamento, um clique dentro da janela do
 * duplo clique depois de uma cópia bem-sucedida é ignorado (o `writeText`
 * resolve em poucos ms, antes do segundo clique do par). Fora da janela, clicar
 * de novo só reinicia os 8 s (edge case da spec).
 */
export const COPY_LOCKOUT_MS = 500;
/** Sonner de erro genérico do fluxo de alinhamento (Figma 16077:31062). */
export const ALIGNMENT_GENERIC_ERROR = 'Algo deu errado. Tente novamente';

export interface ShareAlignmentFormDialogContainerProps {
  programId: string;
  programName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Elemento que recebe o foco ao fechar (o card "Alinhar vagas", AC-14). */
  returnFocusTo?: () => HTMLElement | null;
}

function showGenericError() {
  const id = 'alignment-generic-error';
  toast(ALIGNMENT_GENERIC_ERROR, {
    id,
    duration: 6_000,
    action: { label: 'Fechar', onClick: () => toast.dismiss(id) },
  });
}

/** `navigator.clipboard` ausente (http, permissão, navegador antigo) → lança; nunca falha em silêncio (AC-13). */
async function copyText(text: string): Promise<void> {
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  if (!clipboard || typeof clipboard.writeText !== 'function') {
    throw new Error('clipboard unavailable');
  }
  await clipboard.writeText(text);
}

/**
 * DEV-268 — container do diálogo "Compartilhar formulário": pede o link/token
 * (query idempotente, cacheada por programa — reabrir mostra os MESMOS valores,
 * AC-12), monta a mensagem com a origem do APP Consultor (D1), copia para o
 * clipboard e mantém o rótulo "Mensagem copiada" por 8 s (timer único, limpo ao
 * fechar — `Esc`/"X" no meio da cópia nunca deixa o rótulo travado, ALN-013b).
 *
 * O token NUNCA vai a `console`, URL, log ou telemetria (ALN-013d): ele só
 * existe no estado da query e no texto do diálogo/clipboard.
 */
export function ShareAlignmentFormDialog({
  programId,
  programName,
  open,
  onOpenChange,
  returnFocusTo,
}: ShareAlignmentFormDialogContainerProps) {
  const access = useQuery({ ...alignmentAccessQueryOptions(programId), enabled: open });
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  /** Cópia em andamento (ref, não estado: a guarda precisa ser síncrona). */
  const copying = useRef(false);
  const lastCopiedAt = useRef(0);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);
  useEffect(() => {
    if (!open) {
      // ALN-013b — fechar no meio da cópia: timer limpo, rótulo e trava zerados.
      clearTimer();
      setCopied(false);
      lastCopiedAt.current = 0;
    }
  }, [open, clearTimer]);

  // Sem link/token utilizável não há diálogo para mostrar: Sonner de erro e fecha.
  const unusable = open && (access.isError || (access.data !== undefined && !access.data.token));
  useEffect(() => {
    if (!unusable) return;
    showGenericError();
    onOpenChange(false);
  }, [unusable, onOpenChange]);

  const token = access.data?.token ?? null;
  const message =
    token && access.data
      ? buildAlignmentInviteMessage({
          programName,
          link: buildAlignmentFormLink(window.location.origin, access.data.path),
          token,
        })
      : '';

  async function handleCopy() {
    if (!message || copying.current) return;
    if (Date.now() - lastCopiedAt.current < COPY_LOCKOUT_MS) return;
    copying.current = true;
    try {
      await copyText(message);
    } catch {
      showGenericError();
      return;
    } finally {
      copying.current = false;
    }
    lastCopiedAt.current = Date.now();
    clearTimer();
    setCopied(true);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setCopied(false);
    }, COPIED_LABEL_MS);
  }

  return (
    <ShareAlignmentFormDialogView
      open={open}
      onOpenChange={onOpenChange}
      view={message ? 'ready' : 'loading'}
      message={message}
      copied={copied}
      onCopy={() => void handleCopy()}
      onCloseAutoFocus={(event) => {
        const target = returnFocusTo?.();
        if (!target) return;
        event.preventDefault();
        target.focus();
      }}
    />
  );
}
